type Prunable = Record<string, unknown> | unknown[] | unknown;

/**
 * Chaves que não são conteúdo por si só: um nó que sobra apenas com `@type`
 * declara um tipo e não diz mais nada, então também é vazio. `@id` não entra
 * aqui — uma referência `{ "@id": ... }` é um nó legítimo.
 */
const TYPE_ONLY_KEYS = new Set(["@type"]);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Poda recursivamente valores sem conteúdo — `null`, `undefined`, string vazia
 * (ou só espaços), array vazio e objeto sem chaves.
 *
 * Existe para o critério "qualquer propriedade sem dado correspondente deve ser
 * omitida": o grafo é montado de forma direta e a poda acontece uma vez no fim,
 * em vez de espalhar `if` por cada propriedade.
 */
export function omitEmpty<T extends Prunable>(value: T): T | undefined {
  if (value === null || value === undefined) return undefined;

  if (typeof value === "string") {
    const trimmed = value.trim();
    return (trimmed === "" ? undefined : (trimmed as unknown as T));
  }

  if (Array.isArray(value)) {
    const items = value
      .map((item) => omitEmpty(item))
      .filter((item) => item !== undefined);

    return (items.length === 0 ? undefined : (items as unknown as T));
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value)
      .map(([key, item]) => [key, omitEmpty(item)] as const)
      .filter(([, item]) => item !== undefined);

    const hasContent = entries.some(([key]) => !TYPE_ONLY_KEYS.has(key));

    return (hasContent
      ? (Object.fromEntries(entries) as unknown as T)
      : undefined);
  }

  return value;
}
